// react imports
import { useEffect, useState } from "react";
// material ui imports
import {
  List,
  ListItem,
  ListItemButton,
  Avatar,
  ListItemText,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import GroupIcon from "@mui/icons-material/Group";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import WebStoriesIcon from "@mui/icons-material/WebStories";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
// custom components imports
import { Header } from "../../Header";
import { HeaderMobile } from "../../Header/HeaderMobile";
// react router imports
import { Outlet, Link, useNavigate } from "react-router-dom";
// assets imports
import noAutoLogo from "../../../assets/no-auto-logo.png";
import userImage from "../../../assets/blank-user.jpg";
// redux imports
import { setActiveOrganization } from "../../../store/slices/gammaSlice";
import {
  appLogosUpdate,
  uiOrganizationLogo,
} from "../../../store/slices/localSlice";
import { useSelector, useDispatch } from "react-redux";
// utils imports
import Swal from "sweetalert2";
import s from "./NavBarAdmin.module.css";
import { useTranslation } from "react-i18next";
import themeService from "../../../api/themes/ThemeService";
import { STATUS_OK } from "../../../config/strings";
import { useAuth } from "../../Auth/AuthProvider";
import { useJwt } from "react-jwt";
import { useNotificationContext } from "../../Notifications/CustomNotifications";

// Text style
const text = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "0.85em",
  fontWeight: "300",
};

// =========================== | NAV BAR ADMIN | ===============================

export const NavBarAdmin = () => {
  const notification = useNotificationContext();
  // Section: Hooks
  const [userName, setUserName] = useState("");
  const { textColor, appLogos } = useSelector((state) => state.local);
  const [selectedLotery, setSelectedLotery] = useState("");
  const [selected, setSelected] = useState({});
  const dispatch = useDispatch();
  // const superusuario = keycloak.realmAccess.roles.includes(roles.superusuario)
  const currentUser = useSelector((state) => state.gamma.logged_user);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [openNav, setOpenNav] = useState(
    window.innerWidth < 1000 ? false : true,
  );
  // let navigate = useNavigate();
  const navigate = useNavigate();

  // session hook
  const { session } = useAuth();
  const { decodedToken } = useJwt(session.accessToken);

  // Section: Effects
  useEffect(() => {
    if (decodedToken) {
      setUserName(decodedToken.name);
    }
  }, [decodedToken]);

  if (!appLogos?.length) {
    themeService
      .getAllLogos(dispatch)
      .then((response) => {
        dispatch(appLogosUpdate(response));
      })
      .catch((error) => {
        console.log(error);
        notification.error("error ep", error.message);
      });
  }

  // Section: Handlers
  const handleChange = (e) => {
    setSelectedLotery(e.target.value);
    dispatch(setActiveOrganization(e.target.value));
    const newLogo = appLogos.find(
      (logo) => logo.organizationId === e.target.value.id,
    );
    themeService
      .setDefaultLogo(newLogo.id, e.targe.value.id)
      .then((response) => {
        console.log(response);
        dispatch(uiOrganizationLogo(newLogo));
      })
      .catch((error) => {
        console.log(error);
        notification.error("error ep", error.message);
      });
  };

  const openSubMenu = () => {
    setOpen(!open);
  };

  const openNavMenu = () => {
    setOpenNav(!openNav);
  };

  async function logOutSession() {
    navigate("/logout"); // routing to the login
  }

  const logOut = () => {
    // let path = `/logout`;
    Swal.fire({
      title: t("logoutDialog"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4BB544",
      cancelButtonColor: "#d33",
      cancelButtonText: t("cancel"),
      confirmButtonText: t("accept"),
    }).then((result) => {
      if (result.isConfirmed) {
        logOutSession();
      }
    });
  };

  return (
    <div className={s.container}>
      <Grid
        className={`${s.navbar} ${openNav ? s.open : null}`}
        sx={{ bgcolor: "primary.main" }}
        item
      >
        <div className={s.navContent}>
          <div className={s.navHeader}>
            <img className={s.abaco_logo} src={noAutoLogo} alt="Logo Abaco" />
            <div className={s.avatar}>
              <Avatar
                sx={{ width: 40, height: 40 }}
                alt="Profile picture"
                src={userImage}
                className={s.profilepic}
              ></Avatar>
              <h3 className={s.username} style={{ color: textColor }}>
                {userName}
              </h3>
            </div>
          </div>
          <Divider className={s.divider} />
          <nav aria-label="navigation" className={s.navList}>
            <List>
              <ListItem
                className={s.itemContainer}
                onClick={() => {
                  window.innerWidth < 1000 && setOpenNav(false);
                  setSelected({ users: true });
                }}
              >
                <Link
                  className={`${s.listItem} ${selected.users ? s.selectedItem : null}`}
                  to={`users`}
                >
                  <ListItemButton className={s.itemButton}>
                    <GroupIcon
                      sx={{ color: "secondary.main", mr: 1 }}
                      className={s.itemIcon}
                    />
                    <ListItemText
                      primary={t("users")}
                      sx={{ color: textColor }}
                      primaryTypographyProps={{ style: text }}
                      className={s.itemText}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
              <ListItem
                className={s.itemContainer}
                onClick={() => {
                  window.innerWidth < 1000 && setOpenNav(false);
                  setSelected({ points: true });
                }}
              >
                <Link
                  className={`${s.listItem} ${selected.points ? s.selectedItem : null}`}
                  to={`sales_points`}
                >
                  <ListItemButton className={s.itemButton}>
                    <PointOfSaleIcon
                      sx={{ color: "secondary.main", mr: 1 }}
                      className={s.itemIcon}
                    />
                    <ListItemText
                      primary={t("points")}
                      sx={{ color: textColor }}
                      primaryTypographyProps={{ style: text }}
                      className={s.itemText}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
              <ListItem
                className={s.itemContainer}
                onClick={() => {
                  window.innerWidth < 1000 && setOpenNav(false);
                  setSelected({ games: true });
                }}
              >
                <Link
                  className={`${s.listItem} ${selected.games ? s.selectedItem : null}`}
                  to={`games-and-logos`}
                >
                  <ListItemButton className={s.itemButton}>
                    <WebStoriesIcon
                      sx={{ color: "secondary.main", mr: 1 }}
                      className={s.itemIcon}
                    />
                    <ListItemText
                      primary={t("games")}
                      sx={{ color: textColor }}
                      primaryTypographyProps={{ style: text }}
                      className={s.itemText}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
              <ListItem
                className={s.itemContainer}
                onClick={() => {
                  window.innerWidth < 1000 && setOpenNav(false);
                  setSelected({ config: true });
                }}
              >
                <Link
                  className={`${s.listItem} ${selected.config ? s.selectedItem : null}`}
                  to={`configure`}
                >
                  <ListItemButton className={s.itemButton}>
                    <SettingsIcon
                      sx={{ color: "secondary.main", mr: 1 }}
                      className={s.itemIcon}
                    />
                    <ListItemText
                      primary={t("configure")}
                      sx={{ color: textColor }}
                      primaryTypographyProps={{ style: text }}
                      className={s.itemText}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
              <ListItem className={s.itemContainer}>
                <div className={s.listItem}>
                  <ListItemButton
                    onClick={openSubMenu}
                    className={s.itemButton}
                  >
                    <ViewQuiltIcon
                      sx={{ color: "secondary.main", mr: 1 }}
                      className={s.itemIcon}
                    />
                    <ListItemText
                      sx={{ color: textColor }}
                      primary={t("themes")}
                      primaryTypographyProps={{ style: text }}
                      className={s.itemText}
                    />
                    {open ? (
                      <ExpandLess sx={{ color: textColor }} />
                    ) : (
                      <ExpandMore sx={{ color: textColor }} />
                    )}
                  </ListItemButton>
                </div>
              </ListItem>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem
                    className={s.itemContainer}
                    onClick={() => {
                      window.innerWidth < 1000 && setOpenNav(false);
                      setSelected({ colors: true });
                    }}
                  >
                    <Link
                      className={`${s.listItem} ${selected.colors ? s.selectedItem : null}`}
                      to={`colors`}
                      style={{ width: "80%" }}
                    >
                      <ListItemButton className={s.itemButton}>
                        <ListItemText
                          sx={{ color: textColor, ml: 4 }}
                          primary={t("colors")}
                          primaryTypographyProps={{ style: text }}
                          className={s.itemText}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem
                    className={s.itemContainer}
                    onClick={() => {
                      window.innerWidth < 1000 && setOpenNav(false);
                      setSelected({ logos: true });
                    }}
                  >
                    <Link
                      className={`${s.listItem} ${selected.logos ? s.selectedItem : null}`}
                      to={`logos`}
                      style={{ width: "80%" }}
                    >
                      <ListItemButton className={s.itemButton}>
                        <ListItemText
                          sx={{ color: textColor, ml: 4 }}
                          primary={t("logos")}
                          primaryTypographyProps={{ style: text }}
                          className={s.itemText}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                </List>
              </Collapse>
              <ListItem className={s.itemContainer}>
                <div className={s.listItem}>
                  <ListItemButton onClick={logOut} className={s.itemButton}>
                    <LogoutIcon
                      sx={{ color: "secondary.main", mr: 1 }}
                      className={s.itemIcon}
                    />
                    <ListItemText
                      primary={t("logOut")}
                      sx={{ color: textColor }}
                      primaryTypographyProps={{ style: text }}
                      className={s.itemText}
                    />
                  </ListItemButton>
                </div>
              </ListItem>
            </List>
          </nav>
        </div>

        <div className={s.arrowNav}>
          <div className={s.arrowIcon} onClick={openNavMenu}>
            <ArrowBackIosIcon sx={{ fontSize: 50, color: "secondary.main" }} />
          </div>
        </div>
      </Grid>

      <div className={`${s.content} ${openNav ? s.openContent : null}`}>
        <div className={s.headerPos}>
          <div className={s.mobileHeader}>
            <HeaderMobile openNav={openNavMenu} />
          </div>
          <div className={s.webHeader}>
            <Header />
          </div>
        </div>
        <div className={s.outletPos}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};
